'use client'

import * as React from 'react'
import { List, Map } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSearchStore } from '@/lib/store'
import { useI18n } from '@/lib/i18n'

export function MobileViewToggle() {
  const { viewMode, setViewMode } = useSearchStore()
  const { t } = useI18n()
  // A viewport-fixed pill sits on whatever is at the bottom of the screen —
  // at the end of the results that's the footer, so the pill covered its
  // links. Fade out once the footer is on screen; the user has left the
  // results by then and the toggle has nothing left to switch between.
  // Starts `true` so the server and first client render agree (a capability/
  // measurement gate computed inline is the hydration bug this codebase has
  // already been bitten by).
  const [clearOfFooter, setClearOfFooter] = React.useState(true)

  React.useEffect(() => {
    const footer = document.querySelector('footer')
    if (!footer || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver((entries) => setClearOfFooter(!entries.some((e) => e.isIntersecting)), {
      rootMargin: '0px 0px -8px 0px',
    })
    io.observe(footer)
    return () => io.disconnect()
  }, [])

  const handleToggle = () => {
    setViewMode(viewMode === 'list' ? 'map' : 'list')
  }

  const Icon = viewMode === 'list' ? Map : List
  const label = viewMode === 'list' ? t('search.map', 'Map') : t('search.list', 'List')
  
  return (
    <button
      type="button"
      onClick={handleToggle}
      className={cn(
        // Only show on mobile/tablet
        'lg:hidden',
        // Floating position. `bottom-6` alone put the pill under the iOS home
        // indicator (34px) in standalone/PWA mode; clear the inset the way the
        // listing-detail price bar already does.
        'fixed bottom-[max(24px,calc(env(safe-area-inset-bottom)+10px))] left-1/2 -translate-x-1/2 z-50',
        // Pill style
        'flex items-center gap-2 px-5 py-3 rounded-full',
        // Dark pill with white text (like Airbnb)
        'bg-foreground text-background',
        // Shadow and effects
        'shadow-lg shadow-black/20',
        // Hover/active states
        'hover:scale-105 active:scale-95',
        'transition-all duration-200',
        !clearOfFooter && 'pointer-events-none translate-y-3 opacity-0'
      )}
      aria-hidden={!clearOfFooter}
    >
      <Icon className="h-4 w-4" />
      <span className="text-sm font-semibold">{label}</span>
    </button>
  )
}

